// src/components/outputs/pieces/QualityBar.jsx — the QA score bar of a history card.
//
// Lifted from the former per-format renderers (deleted in phase 5 of the
// output-structure plan; all drew the same bar) so the one piece-list renderer
// keeps the same source of truth: the caller passes `score`, read as
// `results.metadata.qa_score ?? results.metadata.style_compliance ?? null`.
// Thresholds are unchanged: ≥ 70 verde, ≥ 50 attenzione, else errore. The fill
// is clamped to 0–100; a null or non-numeric score renders NOTHING (null means
// unknown, never zero).
// One deliberate difference from the renderers: the track was `w(0.03)` (a
// white tint, invisible on the console's light surface); here it is the theme's
// field colour (t.inputBg, else COLORS.field) so RunDetailPage's light theme
// gets a visible track — which is why `w` is not taken.
//
//   QualityBar({ score, t })   t = the theme object of the surface (t.inputBg
//                              for the track; falls back to COLORS.field)

import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_MONO, RADII, SEMANTIC } from '../../../constants/theme.js';
import { SectionLabel } from '../OutputPrimitives.jsx';

/** The bar colour of a score — the thresholds every renderer used. */
export const qaColor = (score) =>
  score >= 70 ? SEMANTIC.verde.fg : score >= 50 ? SEMANTIC.attenzione.fg : SEMANTIC.errore.fg;

const QualityBar = ({ score, t = {} }) => {
  const { t: tr } = useTranslation('outputs');
  const n = typeof score === 'number' && Number.isFinite(score) ? score : null;
  if (n == null) return null;
  const color = qaColor(n);
  const width = Math.min(100, Math.max(0, n));

  return (
    <div style={{ marginBottom: '18px' }} data-testid="quality-bar">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '6px',
        }}
      >
        <SectionLabel t={t}>{tr('common.qaScore')}</SectionLabel>
        <span
          style={{
            fontSize: '13px',
            fontWeight: 700,
            color,
            fontFamily: FONT_MONO,
          }}
        >
          {n}/100
        </span>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={width}
        style={{
          height: '6px',
          borderRadius: RADII.controlInner,
          background: t.inputBg || COLORS.field,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${width}%`,
            height: '100%',
            background: color,
            transition: 'width 0.4s ease',
          }}
        />
      </div>
    </div>
  );
};

export default QualityBar;
